import { action, computed, observable } from 'mobx'
import {
  func,
  type IValidationSchema,
  required,
  validationContext,
} from 'validx'
import type { LegendPartDependencies } from '@/features/legends/editor/legend-part-dependencies'
import { LegendPartViewModelBase } from '@/features/legends/editor/LegendPartViewModelBase'
import {
  type ErrorDescriptor,
  receiveErrors,
} from '@/features/legends/editor/analyzer-errors'
import { newCid } from '@/features/legends/editor/collections'
import type { SymbolContext } from '@/features/legends/editor/entities'
import { Types } from '@/features/legends/editor/ryno-types'
import { validateWhileEditing } from '@/features/legends/editor/validation'
import { FiltersBuilderViewModel } from '@/features/legends/filters/FiltersBuilderViewModel'
import { RynoEditorViewModel } from '@/features/legends/ryno/RynoEditorViewModel'
import { validateObject } from '@/utils/validx-util'
import type { InfoLine, InfoLineDesignee } from './legend-options'

export const designees: InfoLineDesignee[] = ['Both', 'Client', 'Provider']

/**
 * A tab of the info line inspector.
 */
export type InfoLineTab = 'General' | 'Conditions'

/**
 * A line of extra context shown on the job, computed by a formula.
 */
export class InfoLineEditorViewModel extends LegendPartViewModelBase {
  private readonly _context: SymbolContext
  private readonly _schema: IValidationSchema<InfoLineEditorViewModel> = {
    title: [required('Title is required.')],
    content: [func(() => this.content.analyze(), 'There are formula errors.')],
    designee: [
      required(),
      func(
        ({ value }) => designees.some((designee) => designee === value),
        'Not a valid designee',
      ),
    ],
    triggerWhen: [validateObject('There are invalid conditions.')],
  }

  /**
   * Identifies the line on the client, for keys.
   */
  readonly cid = newCid('info-line')

  /**
   * When the line shows. No conditions means always.
   */
  readonly triggerWhen: FiltersBuilderViewModel

  /**
   * The formula that computes the line's text.
   */
  readonly content: RynoEditorViewModel

  @observable accessor title = ''
  @observable accessor designee: InfoLineDesignee = 'Both'

  readonly validation = validationContext(this, this._schema)

  constructor(dependencies: LegendPartDependencies, context: SymbolContext) {
    super(dependencies)
    this._context = context
    this.triggerWhen = new FiltersBuilderViewModel({
      dependencies,
      context,
      scope: [],
    })
    this.content = new RynoEditorViewModel({
      required: true,
      getSymbolTable: () => this._symbolTable,
      getNeededType: () => Types.string,
    })
    this.lifecycle.manage(this.triggerWhen)
    this.lifecycle.manage(this.content)
    this.lifecycle.manage(validateWhileEditing(this, this._schema))
  }

  /**
   * The content formula's last expression, as a summary of the line.
   */
  @computed
  get lastContentLine() {
    return this.content.lastLine
  }

  @computed
  private get _symbolTable() {
    return this.dependencies.symbols.getSymbolTableFor(this._context)
  }

  /**
   * The first validation error of the fields on the inspector tab, if any.
   *
   * @param tab
   */
  getTabError(tab: InfoLineTab) {
    switch (tab) {
      case 'General':
        return (
          this.validation.getError('title') ??
          this.validation.getError('content')
        )
      case 'Conditions':
        return this.validation.getError('triggerWhen')
    }
  }

  /**
   * Sets the line from the legend document.
   */
  @action
  hydrate(line: InfoLine) {
    this.triggerWhen.hydrate(line.triggerWhen)
    this.content.setValue(line.content)
    this.title = line.title ?? ''
    this.designee = line.designee ?? 'Both'
  }

  /**
   * Shows the analyzer's errors on the line.
   */
  receiveErrors(descriptor: ErrorDescriptor) {
    receiveErrors(this, descriptor.inner)
  }

  /**
   * The line as the legend document has it.
   */
  toJSON(): InfoLine {
    return {
      title: this.title,
      content: this.content.toJSON(),
      designee: this.designee,
      triggerWhen: this.triggerWhen.toJSON(),
    }
  }
}
